import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { api } from '../lib/api';
import { setPrefs, usePrefs, useToggleTheme, type Palette, type Texture } from '../lib/prefs';
import { keys, useSession } from '../lib/queries';
import * as Ic from './icons';

/* ─────────────────────────────────────────────────────────────────────
   A moldura: trilho, barra superior, o fio da jornada, o palco e a
   barra de ação. Cada tela declara o próprio caminho (crumb), o ponto
   da jornada e as próprias ações — a moldura só desenha.
   ───────────────────────────────────────────────────────────────────── */

export type NavKey = 'inicio' | 'strategy' | 'dna' | 'studio' | 'pulse' | 'biblioteca' | 'config';
export interface ViewMeta { nav: NavKey; crumb: [ReactNode, ReactNode]; j: number }

interface ShellApi {
  setMeta: (m: ViewMeta) => void;
  actSlot: HTMLElement | null;
  stage: React.RefObject<HTMLElement | null>;
}
const ShellContext = createContext<ShellApi | null>(null);
const useShell = () => {
  const s = useContext(ShellContext);
  if (!s) throw new Error('fora do Shell');
  return s;
};

/** A tela diz onde está. */
export function useViewMeta(meta: ViewMeta) {
  const { setMeta } = useShell();
  const key = JSON.stringify([meta.nav, String(meta.crumb[0]), typeof meta.crumb[1] === 'string' ? meta.crumb[1] : '', meta.j]);
  useLayoutEffect(() => { setMeta(meta); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** As ações da tela, na barra de baixo. */
export function Actions({ children }: { children: ReactNode }) {
  const { actSlot } = useShell();
  return actSlot ? createPortal(children, actSlot) : null;
}

export function useStage() { return useShell().stage; }

const NAV: Array<{ key: NavKey; to: string; label: string; icon: (p: object) => ReactNode }> = [
  { key: 'inicio', to: '/inicio', label: 'Início', icon: Ic.Home },
  { key: 'strategy', to: '/strategy', label: 'Strategy', icon: Ic.Shield },
  { key: 'dna', to: '/dna', label: 'DNA', icon: Ic.Globe },
  { key: 'studio', to: '/studio', label: 'Studio', icon: Ic.Nodes },
  { key: 'pulse', to: '/pulse', label: 'Pulse', icon: Ic.Bars },
  { key: 'biblioteca', to: '/biblioteca', label: 'Biblioteca', icon: Ic.Book },
];

const PALETTES: Array<{ p: Palette; n: string; sw: string; label: string }> = [
  { p: 'sinal', n: 'Sinal', sw: '#d9600f', label: 'Sinal — laranja Braun sobre grafite quente' },
  { p: 'grafite', n: 'Grafite', sw: '#3a2e26', label: 'Grafite — sem cor na interface' },
  { p: 'floresta', n: 'Floresta', sw: '#1d754c', label: 'Floresta — verde profundo' },
  { p: 'azul', n: 'Azul', sw: '#005cff', label: 'Azul — a marca atual do deck' },
];
const TEX: Texture[] = ['none', 'campo', 'foto'];
const TEX_LABEL: Record<Texture, string> = { none: 'Liso', campo: 'Luz', foto: 'Foto' };

export function Shell() {
  const { me } = useSession();
  const prefs = usePrefs();
  const toggleTheme = useToggleTheme();
  const nav = useNavigate();
  const qc = useQueryClient();
  const loc = useLocation();
  const stage = useRef<HTMLElement>(null);
  const [actSlot, setActSlot] = useState<HTMLElement | null>(null);
  const [meta, setMeta] = useState<ViewMeta>({ nav: 'inicio', crumb: ['', ''], j: 0 });
  const [menu, setMenu] = useState(false);
  const [projMenu, setProjMenu] = useState(false);
  const { projectId } = useSession();

  /* troca de tela: o palco volta ao topo */
  useEffect(() => { stage.current?.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior }); }, [loc.pathname]);

  /* ⏎ aciona a ação principal; ⇧⏎ a secundária */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.isComposing || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target as HTMLElement).tagName;
      if (['TEXTAREA', 'SELECT', 'BUTTON', 'A', 'SUMMARY'].includes(tag)) return;
      const btn = document.querySelector<HTMLButtonElement>(e.shiftKey ? '.actions .btn-ghost:not(:disabled)' : '.actions .btn-primary:not(:disabled)');
      if (!btn) return;
      e.preventDefault();
      btn.focus(); btn.click();
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, []);

  const logout = useCallback(async () => {
    await api('/auth/logout', { method: 'POST' }).catch(() => {});
    qc.clear();
    nav('/login', { replace: true });
  }, [nav, qc]);

  const credits = me.org.credits;
  const palName = PALETTES.find(p => p.p === prefs.pal)?.n ?? 'Sinal';

  return (
    <ShellContext.Provider value={{ setMeta, actSlot, stage }}>
      <div className="app">
        <aside className="rail">
          <NavLink className="lockup" to="/inicio" aria-label="ADS — Aion Design System">
            <span className="mark">ADS</span><span className="sub">Aion Design System</span>
          </NavLink>
          <nav className="nav" aria-label="Principal">
            {NAV.map(n => (
              <NavLink key={n.key} to={n.to} className="nav-item" aria-current={meta.nav === n.key ? 'page' : undefined}>
                <n.icon /><span>{n.label}</span>
              </NavLink>
            ))}
          </nav>
          <nav className="nav" aria-label="Sistema">
            <NavLink to="/configuracoes" className="nav-item" aria-current={meta.nav === 'config' ? 'page' : undefined}>
              <Ic.Gear /><span>Configurações</span>
            </NavLink>
            <button type="button" className="nav-item" onClick={logout}><Ic.Logout /><span>Sair</span></button>
          </nav>
          <div className="rail-foot"><span className="mark">ADS</span><span className="ver mono">v{__APP_VERSION__}</span></div>
        </aside>

        <div className="main">
          <header className="topbar">
            <div className="crumb"><span>{meta.crumb[0]}</span><span className="sep">/</span><b>{meta.crumb[1]}</b></div>
            <div className="spacer" />

            <div className="who-wrap">
              <button className="chip" type="button" aria-haspopup="menu" aria-expanded={projMenu} onClick={() => setProjMenu(m => !m)}>
                <span className="k">Projeto</span><span>{me.org.name}</span><Ic.ChevronDown />
              </button>
              {projMenu && (
                <div className="menu menu-left" role="menu" onMouseLeave={() => setProjMenu(false)}>
                  <div className="menu-h"><b>{me.org.name}</b><span>{me.org.role === 'owner' ? 'Você administra esta organização' : 'Membro da organização'}</span></div>
                  {me.projects.map(pr => (
                    <button key={pr.id} type="button" role="menuitemradio" aria-checked={pr.id === projectId} onClick={() => setProjMenu(false)}>
                      <Ic.Check className={pr.id === projectId ? '' : 'off'} /><span className="menu-proj"><b>{pr.name}</b><em>Ciclo {pr.cycle}</em></span>
                    </button>
                  ))}
                  <button type="button" role="menuitem" onClick={() => { setProjMenu(false); nav('/configuracoes'); }}><Ic.Gear />Design system e publicação</button>
                </div>
              )}
            </div>

            {/* quanto ainda dá para gerar: começa cheio e esvazia a cada peça */}
            <button type="button" className="meter mono" onClick={() => nav('/configuracoes?aba=uso')}
              title={`${Math.max(0, credits.quota - credits.used).toLocaleString('pt-BR')} de ${credits.quota.toLocaleString('pt-BR')} tokens do ADS disponíveis nesta organização. Cada agente usa um valor fixo. Clique para ver o uso e o gasto na Anthropic.`}>
              <span className="label">Tokens</span>
              <div className="track"><i style={{ width: `${Math.max(0, Math.min(100, ((credits.quota - credits.used) / Math.max(1, credits.quota)) * 100))}%` }} /></div>
              <span className="val">{Math.max(0, credits.quota - credits.used)}<span className="den">/{credits.quota}</span></span>
            </button>

            <div className="vdiv" />

            <div className="pal mono" role="group" aria-label="Estudo de cor">
              <span className="nm">{palName}</span>
              <span className="dots">
                {PALETTES.map(p => (
                  <button key={p.p} type="button" aria-pressed={prefs.pal === p.p} aria-label={p.label}
                    style={{ '--sw': p.sw } as CSSProperties} onClick={() => setPrefs({ pal: p.p })} />
                ))}
              </span>
              <button className="tex" type="button" aria-label="Alternar textura de fundo"
                onClick={() => setPrefs({ tex: TEX[(TEX.indexOf(prefs.tex) + 1) % TEX.length]! })}>{TEX_LABEL[prefs.tex]}</button>
            </div>

            <div className="vdiv" />
            <div className="who-wrap">
              <button className="who" type="button" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(m => !m)}>
                <span className="avatar">{me.user.initials}</span><span className="nm">{me.user.name}</span>
              </button>
              {menu && (
                <div className="menu" role="menu" onMouseLeave={() => setMenu(false)}>
                  <div className="menu-h"><b>{me.user.name}</b><span>{me.user.email}</span></div>
                  <button type="button" role="menuitem" onClick={() => { setMenu(false); nav('/configuracoes'); }}><Ic.Gear />Design system</button>
                  <button type="button" role="menuitem" onClick={logout}><Ic.Logout />Sair</button>
                </div>
              )}
            </div>

            <button className="icon-btn" type="button" aria-label="Alternar tema" onClick={toggleTheme}>
              <Ic.Sun /><Ic.Moon />
            </button>
          </header>
          <div className="journey" aria-hidden="true" style={{ '--j': meta.j } as CSSProperties} />

          <main className="stage" ref={stage}>
            <div className="sheet">
              <Outlet />
            </div>
          </main>

          <div className="actions">
            <span className="secure mono"><Ic.ShieldCheck /><span>Dados restritos a esta organização</span></span>
            <div className="keys">
              <span className="keyhint"><kbd className="mono">⏎</kbd>Continuar</span>
              <span className="keyhint"><kbd className="mono">⇧</kbd><kbd className="mono">⏎</kbd>Ação secundária</span>
            </div>
            <div className="spacer" />
            <div className="act-slot" ref={setActSlot} />
          </div>
        </div>
      </div>
    </ShellContext.Provider>
  );
}

/** Invalida a sessão (créditos) — usado depois de gerar. */
export function useRefreshMe() {
  const qc = useQueryClient();
  return useCallback(() => qc.invalidateQueries({ queryKey: keys.me }), [qc]);
}
